/*
 * sr-hover-image.js
 *
 * SR FIX - PERFORMANCE (2026-08-22)
 *
 * Product cards render a secondary ("pallu") image that theme.css keeps at
 * `display: none` and only promotes to `display: block; opacity: 0` inside
 * `@media screen and (pointer: fine)`, fading it in on
 * `.product-card__figure:hover`. Touch visitors therefore never see it at all,
 * and desktop visitors only see the ones they hover - yet every one of them
 * used to download with the page. On the live homepage that measured
 * 36 images / 6.54 MB out of a ~13 MB total.
 *
 * snippets/product-card.liquid now emits that <img> with its src/srcset parked
 * in data-sr-src / data-sr-srcset. This promotes them on the first hover or
 * keyboard focus of a card, so the image still appears - just paid for at the
 * moment it is actually wanted.
 *
 * Notes:
 * - Delegated on document, so cards injected later (section rendering API,
 *   filtering, infinite scroll, quick-view) are covered with no re-binding.
 * - pointerover is used rather than pointerenter because pointerenter does
 *   not bubble.
 * - The `(pointer: fine)` test mirrors the CSS exactly and is evaluated per
 *   event, not once at boot, so a tablet that gains a mouse still works and a
 *   touch tap never triggers a pointless download.
 * - srcset is set before src so the browser picks the right candidate rather
 *   than fetching the fallback first.
 */
(function () {
  'use strict';

  var SELECTOR = 'img.product-card__image--secondary[data-sr-src]';

  function pointerIsFine() {
    if (!window.matchMedia) return true;
    return window.matchMedia('screen and (pointer: fine)').matches;
  }

  function promote(img) {
    var srcset = img.getAttribute('data-sr-srcset');
    var src = img.getAttribute('data-sr-src');

    img.removeAttribute('data-sr-srcset');
    img.removeAttribute('data-sr-src');

    // `loading="lazy"` MUST be dropped before the source is assigned.
    // Chrome settles an img's lazy-loading state when it is first parsed; an
    // img parsed with no src is settled as "nothing to defer", and assigning
    // src later does not re-arm the deferral - the image then never loads,
    // even fully in view. Verified on /collections/silk-sarees: in-viewport,
    // src and srcset both set, naturalWidth stayed 0 until `loading` was
    // flipped to eager, at which point it decoded immediately.
    // (The homepage masked this: the carousel's own _preloadImages() strips
    // `loading` from the previous/next slide, so those cards worked anyway.)
    // Eager is right here regardless - we only get called on real hover or
    // focus, i.e. the moment the image is actually wanted.
    img.setAttribute('loading', 'eager');
    img.removeAttribute('fetchpriority');

    if (srcset) img.setAttribute('srcset', srcset);
    if (src) img.setAttribute('src', src);
  }

  function handle(event) {
    if (!pointerIsFine()) return;

    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var figure = target.closest('.product-card__figure');
    if (!figure) return;

    var images = figure.querySelectorAll(SELECTOR);
    for (var i = 0; i < images.length; i++) promote(images[i]);
  }

  var options = { passive: true, capture: true };
  document.addEventListener('pointerover', handle, options);
  document.addEventListener('focusin', handle, options);
})();
